/**
 * Connector Attest Page (M-PILOT-03 WAVE 5, ADR-009 Mission Trust manual connector)
 *
 * The Mission Trust advisor surface: the firm-scoped direct-attested connector queue
 * (GET /api/v1/advisor/connector/queue on advisor-tools-api), a per-member keystroke form
 * for the member's investment positions, a step-up modal (re-assert TOTP + fiduciary
 * attestation), and the attest submit (POST /api/financial/connector/attest on consumer-api).
 *
 * The attest write requires an aaim-core advisor token (aud=aaim-platform, mfaVerified,
 * minted within 15 minutes). The step-up modal re-mints that token in place via
 * useAuth().stepUp(code) and forwards it on the attest POST, so the advisor's keystroked
 * form state survives the freshness re-challenge.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Heading,
  BodyText,
  DataTable,
  Button,
  Input,
  Select,
  Checkbox,
  Dialog,
  Badge,
  FormLabel,
  type DataTableColumn,
  type SelectOption,
} from '@aaimplatform/ui';
import { advisorClient, connectorAttestClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import type { components as AdvisorComponents } from '@aaimplatform/institutional-types/advisor-tools';
import type { components as FinancialComponents } from '@aaimplatform/institutional-types/financial-provider';
import type { components as AssetClassificationComponents } from '@aaimplatform/institutional-types/asset-classification';

// Canonical 13 Aaim LTV-baseline asset classes (generated enum -- OpenAPI-first).
type LtvAssetClass = AssetClassificationComponents['schemas']['LtvAssetClass'];
// Operator-facing labels + the baseline LTV range (from the canonical taxonomy,
// calibrated to Reg T/U + OCC + FDIC + UCC 8/9/12). Display only; the FI sets
// the actual terms. Order matches the canonical high-to-low LTV ordering. Typed
// as { value: LtvAssetClass } so a typo in any of the 13 values is a compile
// error; widened to SelectOption[] at the Select call site (value is a string).
const ASSET_CLASS_OPTIONS: ReadonlyArray<{ value: LtvAssetClass; label: string }> = [
  { value: 'STABLECOINS', label: 'Stablecoins (80-90%)' },
  { value: 'PUBLIC_SECURITIES', label: 'Public Securities (60-75%)' },
  { value: 'COMMERCIAL_REAL_ESTATE', label: 'Commercial Real Estate (55-70%)' },
  { value: 'MAJOR_CRYPTOCURRENCY', label: 'Major Cryptocurrency - BTC/ETH (50-65%)' },
  { value: 'PUBLIC_REITS', label: 'Public REITs (50-65%)' },
  { value: 'TOKENIZED_SECURITIES', label: 'Tokenized Securities (45-60%)' },
  { value: 'PRIVATE_CREDIT', label: 'Private Credit (40-55%)' },
  { value: 'NON_TRADED_REITS', label: 'Non-Traded REITs (35-55%)' },
  { value: 'LP_INTERESTS_HEDGE_FUND', label: 'LP Interests - Hedge Funds (30-50%)' },
  { value: 'LP_INTERESTS_PRIVATE_EQUITY', label: 'LP Interests - Private Equity (25-45%)' },
  { value: 'RSU_RESTRICTED_STOCK', label: 'RSUs / Restricted Stock (25-45%)' },
  { value: 'PRIVATE_COMPANY_EQUITY', label: 'Private Company Equity (20-40%)' },
  { value: 'VENTURE_STARTUP_EQUITY', label: 'Venture / Startup Equity (10-30%)' },
];

type PendingConnectorConnection = AdvisorComponents['schemas']['PendingConnectorConnection'];
type ConnectorAttestedPosition = FinancialComponents['schemas']['ConnectorAttestedPosition'];
type ConnectorAttestResponse = FinancialComponents['schemas']['ConnectorAttestResponse'];

// GH #1262: every keystroked position is attested in USD, and the form has no
// currency control. The pilot is US-only (Robert's ruling 2026-10-23, option 1).
// consumer-api stores no currency on Holding or FinancialAccount and sums
// marketValue into the balance and the capacity math as one currency, so a
// second code would enter the money path unconverted. Currency end to end,
// with an FX source of record, is GH #4015.
const ATTESTED_POSITION_CURRENCY: NonNullable<ConnectorAttestedPosition['currency']> = 'USD';

// Page-local keystroke-row view-model. Kept as an inferred shape (no named interface/type)
// so the row remains an editable string view-model without a manual ad-hoc type declaration;
// the wire contract is the generated ConnectorAttestedPosition, built from these rows on submit.
const EMPTY_ROW = {
  accountName: '',
  tickerSymbol: '',
  securityName: '',
  assetClass: '',
  quantity: '',
  unitPrice: '',
  marketValue: '',
  // Which of unitPrice / marketValue the operator last typed by hand. The OTHER
  // one is the derived field and gets recomputed from quantity. Drives the
  // "recalculated" visual feedback. '' = neither edited yet.
  lastEdited: '' as '' | 'unitPrice' | 'marketValue',
  // Set briefly to the field name that was just auto-recomputed, for a flash of
  // visual feedback so the operator can double-check the derived value.
  recalcFlash: '' as '' | 'unitPrice' | 'marketValue',
  // LP-interest fund economics (GH #1838): the pe-valuation inputs only a
  // human can supply, rendered only for the two LP asset classes. OPTIONAL
  // as a group (absent = the holding rides the static baseline; ADR-014
  // forbids fabricating them) but ALL-OR-NOTHING: a partially-filled trio
  // blocks the row. Capital called is ENTERED as a percent (0-100, how GPs
  // report it) and converted to the 0-1 fraction the wire contract carries.
  feCommitmentAmount: '',
  feCalledCapitalPct: '',
  feCurrentFundNav: '',
  feVintageYear: '',
  feStrategy: '',
};

/** The two asset classes whose rows carry the fund-economics section. */
const LP_CLASSES: ReadonlyArray<string> = ['LP_INTERESTS_PRIVATE_EQUITY', 'LP_INTERESTS_HEDGE_FUND'];

const FUND_STRATEGY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'large_cap_buyout', label: 'Large-cap buyout' },
  { value: 'mid_market_buyout', label: 'Mid-market buyout' },
  { value: 'small_cap_buyout', label: 'Small-cap buyout' },
  { value: 'growth_equity', label: 'Growth equity' },
  { value: 'venture_capital', label: 'Venture capital' },
  { value: 'permanent_capital', label: 'Permanent capital' },
];

/** '' when the trio is untouched; 'partial' when some-but-not-all are valid; 'complete' when all three parse. */
function fundEconomicsState(row: typeof EMPTY_ROW): '' | 'partial' | 'complete' {
  const commitment = row.feCommitmentAmount.trim();
  const called = row.feCalledCapitalPct.trim();
  const nav = row.feCurrentFundNav.trim();
  if (commitment === '' && called === '' && nav === '') return '';
  const commitmentOk = commitment !== '' && Number.isFinite(Number(commitment)) && Number(commitment) > 0;
  const calledOk = called !== '' && Number.isFinite(Number(called)) && Number(called) >= 0 && Number(called) <= 100;
  const navOk = nav !== '' && Number.isFinite(Number(nav)) && Number(nav) >= 0;
  return commitmentOk && calledOk && navOk ? 'complete' : 'partial';
}

type AdvisorError = AdvisorComponents['schemas']['Error'];

/** The 403 code advisor-tools-api returns when the caller's token tenant maps to no active AdvisoryFirm. */
const ADVISOR_FIRM_UNRESOLVED = 'ADVISOR_FIRM_UNRESOLVED';

/**
 * The operator-facing message for a failed queue load.
 *
 * The queue is FIRM-SCOPED (operator ruling 3, 2026-09-09; ADR-009 point 4
 * superseded): advisor-tools-api resolves the caller's token tenant to an active
 * AdvisoryFirm and returns 403 `ADVISOR_FIRM_UNRESOLVED` when it maps to none.
 * The session is valid in that case; the tenant is unregistered. Re-authenticating
 * returns the advisor to the same 403, so this code carries its own message instead
 * of the generic re-auth prompt.
 *
 * `error` is the parsed error body openapi-fetch surfaces, typed from
 * advisor-tools.openapi.yaml (`Error`: required message + optional code).
 */
function queueErrorMessage(error: AdvisorError | undefined): string {
  if (error?.code === ADVISOR_FIRM_UNRESOLVED) {
    return 'The signed-in tenant is not registered as an advisory firm, so the connector queue cannot be scoped. Contact Aaim to register the firm (ADR-009 amendment 2026-09-09).';
  }
  return 'Failed to load the connector queue. Re-authenticate if your session has expired.';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function rowIsComplete(row: typeof EMPTY_ROW): boolean {
  return (
    row.accountName.trim() !== '' &&
    row.tickerSymbol.trim() !== '' &&
    row.securityName.trim() !== '' &&
    row.assetClass.trim() !== '' &&
    row.quantity.trim() !== '' &&
    Number.isFinite(Number(row.quantity)) &&
    Number(row.quantity) > 0 &&
    row.marketValue.trim() !== '' &&
    Number.isFinite(Number(row.marketValue)) &&
    fundEconomicsState(row) !== 'partial'
  );
}

/**
 * The specific reason a row is not yet complete (for the operator-facing
 * affordance that replaces the silently-disabled button). Returns '' if complete.
 */
function rowIncompleteReason(row: typeof EMPTY_ROW): string {
  if (row.accountName.trim() === '') return 'account';
  if (row.tickerSymbol.trim() === '') return 'ticker';
  if (row.securityName.trim() === '') return 'asset name';
  if (row.assetClass.trim() === '') return 'asset class';
  if (row.quantity.trim() === '' || !Number.isFinite(Number(row.quantity)) || Number(row.quantity) <= 0) return 'a positive numeric quantity';
  if (row.marketValue.trim() === '' || !Number.isFinite(Number(row.marketValue))) return 'a numeric market value';
  if (fundEconomicsState(row) === 'partial') {
    return 'complete fund economics (commitment > 0, capital called 0-100%, fund NAV >= 0) or clear all three';
  }
  return '';
}

/** 2-dp money string; '' for non-finite. */
function money(n: number): string {
  return Number.isFinite(n) ? n.toFixed(2) : '';
}
/** up-to-6-dp unit price string; '' for non-finite. */
function unit(n: number): string {
  if (!Number.isFinite(n)) return '';
  return String(Number(n.toFixed(6)));
}

export function ConnectorAttestPage() {
  const { stepUp, canStepUp, mfaOff } = useAuth();

  const [queue, setQueue] = useState<PendingConnectorConnection[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);

  const [selected, setSelected] = useState<PendingConnectorConnection | null>(null);
  const [positions, setPositions] = useState<(typeof EMPTY_ROW)[]>([{ ...EMPTY_ROW }]);
  const [asOfDate, setAsOfDate] = useState<string>(todayIso());

  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [fiduciary, setFiduciary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ConnectorAttestResponse | null>(null);

  const loadQueue = useCallback(async () => {
    setLoadingQueue(true);
    setQueueError(null);
    try {
      const { data, error } = await advisorClient.GET('/api/v1/advisor/connector/queue');
      if (error || !data) {
        setQueueError(queueErrorMessage(error));
        setQueue([]);
      } else {
        setQueue(data);
      }
    } catch {
      setQueueError('Failed to load the connector queue.');
      setQueue([]);
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const selectConnection = (connection: PendingConnectorConnection) => {
    setSelected(connection);
    setPositions([{ ...EMPTY_ROW }]);
    setAsOfDate(todayIso());
    setResult(null);
    setSubmitError(null);
  };

  const updatePosition = (index: number, field: keyof typeof EMPTY_ROW, value: string) => {
    setPositions((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, [field]: value, recalcFlash: '' as typeof row.recalcFlash };

        // Derivation only applies to the qty / unitPrice / marketValue triad.
        if (field !== 'quantity' && field !== 'unitPrice' && field !== 'marketValue') {
          return next;
        }

        const qty = Number(next.quantity);
        const qtyOk = next.quantity.trim() !== '' && Number.isFinite(qty) && qty > 0;

        if (field === 'unitPrice') {
          next.lastEdited = 'unitPrice';
          const up = Number(next.unitPrice);
          if (qtyOk && next.unitPrice.trim() !== '' && Number.isFinite(up)) {
            next.marketValue = money(qty * up);
            next.recalcFlash = 'marketValue';
          }
        } else if (field === 'marketValue') {
          next.lastEdited = 'marketValue';
          const mv = Number(next.marketValue);
          if (qtyOk && next.marketValue.trim() !== '' && Number.isFinite(mv)) {
            next.unitPrice = unit(mv / qty);
            next.recalcFlash = 'unitPrice';
          }
        } else {
          // quantity changed: recompute the DERIVED field, holding the operator's
          // last hand-entered anchor. Anchor unitPrice -> recompute marketValue;
          // anchor marketValue (or no anchor yet) -> recompute unitPrice.
          if (next.lastEdited === 'unitPrice') {
            const up = Number(next.unitPrice);
            if (qtyOk && next.unitPrice.trim() !== '' && Number.isFinite(up)) {
              next.marketValue = money(qty * up);
              next.recalcFlash = 'marketValue';
            }
          } else {
            const mv = Number(next.marketValue);
            if (qtyOk && next.marketValue.trim() !== '' && Number.isFinite(mv)) {
              next.unitPrice = unit(mv / qty);
              next.recalcFlash = 'unitPrice';
            }
          }
        }
        return next;
      }),
    );
  };

  const addPosition = () => setPositions((prev) => [...prev, { ...EMPTY_ROW }]);
  const removePosition = (index: number) =>
    setPositions((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  const completeRows = positions.filter(rowIsComplete);
  const formValid = selected !== null && completeRows.length > 0 && asOfDate.trim() !== '';

  const openStepUp = () => {
    if (!formValid) return;
    setTotpCode('');
    setFiduciary(false);
    setSubmitError(null);
    setStepUpOpen(true);
  };

  const confirmAttest = async () => {
    if (!selected) return;
    if (!fiduciary) {
      setSubmitError('You must affirm the fiduciary attestation to submit.');
      return;
    }
    if (!mfaOff && totpCode.trim().length === 0) {
      setSubmitError('Enter your authenticator code to complete the step-up.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // MFA-enabled advisors re-assert TOTP to re-mint a fresh aud=aaim-platform,
      // mfaVerified SESSION COOKIE (browser-token-storage-hardening bts-02b / GH #1806:
      // the fresh token is delivered ONLY as the HttpOnly cookie, never a body bearer).
      // MFA-off sessions (credentialAuthEnabled=false demo tenants) skip the step-up:
      // their existing session is already mfaVerified=true. Either way the attest
      // authenticates via the same-origin HttpOnly session cookie (credentials:'include',
      // defaulted in connectorAttestClient) -- NO Authorization header is sent. The
      // attest is same-origin (advisor nginx /api/financial -> consumer-api), so the
      // host-only auth-token cookie travels; the connector guard reads it + checks
      // 15-minute freshness from the token's iat claim.
      if (!mfaOff) {
        await stepUp(totpCode.trim());
      }

      const attestPositions: ConnectorAttestedPosition[] = completeRows.map((row) => ({
        accountName: row.accountName.trim(),
        tickerSymbol: row.tickerSymbol.trim(),
        securityName: row.securityName.trim(),
        ...(row.assetClass.trim() ? { assetClass: row.assetClass.trim() } : {}),
        quantity: Number(row.quantity),
        ...(row.unitPrice.trim() && Number.isFinite(Number(row.unitPrice))
          ? { unitPrice: Number(row.unitPrice) }
          : {}),
        marketValue: Number(row.marketValue),
        currency: ATTESTED_POSITION_CURRENCY,
        // GH #1838: the trio is validated all-or-nothing above; percent
        // entry converts to the 0-1 fraction pe-valuation expects.
        ...(fundEconomicsState(row) === 'complete'
          ? {
              fundEconomics: {
                commitmentAmount: Number(row.feCommitmentAmount),
                calledCapitalPercent: Number(row.feCalledCapitalPct) / 100,
                currentFundNav: Number(row.feCurrentFundNav),
                ...(row.feVintageYear.trim() && Number.isInteger(Number(row.feVintageYear))
                  ? { fundVintageYear: Number(row.feVintageYear) }
                  : {}),
                ...(row.feStrategy.trim()
                  ? { fundStrategy: row.feStrategy as NonNullable<ConnectorAttestedPosition['fundEconomics']>['fundStrategy'] }
                  : {}),
              },
            }
          : {}),
      }));

      const { data, error } = await connectorAttestClient.POST('/connector/attest', {
        body: {
          connectionId: selected.connectionId,
          asOfDate: new Date(`${asOfDate}T00:00:00.000Z`).toISOString(),
          fiduciaryAttestation: true,
          positions: attestPositions,
        },
      });

      if (error || !data) {
        const message = (error as { error?: string; message?: string } | undefined)?.error
          ?? (error as { message?: string } | undefined)?.message
          ?? 'Attestation failed.';
        throw new Error(message);
      }

      setResult(data as ConnectorAttestResponse);
      setStepUpOpen(false);
      setSelected(null);
      setPositions([{ ...EMPTY_ROW }]);
      await loadQueue();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Attestation failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const queueColumns: DataTableColumn<PendingConnectorConnection>[] = [
    { header: 'Member', accessor: (row) => row.memberName ?? row.userId },
    { header: 'Email', accessor: (row) => row.memberEmail ?? '' },
    { header: 'Institution', accessor: (row) => row.institutionName ?? row.institutionId ?? '' },
    {
      header: 'Status',
      // JSX cells go in `cell` -- `accessor` returns the value (DataTable
      // String()-ifies a JSX accessor to "[object Object]").
      accessor: (row) => row.status,
      cell: (value) => <Badge>{String(value ?? '')}</Badge>,
    },
    {
      header: 'Authorized',
      accessor: (row) => (row.authorizedAt ? new Date(row.authorizedAt).toLocaleDateString() : ''),
    },
    {
      header: '',
      align: 'right',
      accessor: () => '',
      cell: (_value, row) => (
        <Button variant="secondary" onClick={() => selectConnection(row)}>
          Keystroke positions
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <BodyText>
          Keystroke and attest a consenting member&apos;s investment positions for a pending
          direct-attested connection. Submission re-asserts your authenticator (step-up) and
          records your fiduciary attestation.
        </BodyText>
      </div>

      {result && (
        <Card>
          <Heading level={3} as={3} size="card" className="mb-2">
            Attestation recorded
          </Heading>
          <BodyText>
            Connection {result.connectionId} is now {result.status}. {result.holdingsWritten} holding
            {result.holdingsWritten === 1 ? '' : 's'} written across {result.accountsWritten ?? 0} account
            {(result.accountsWritten ?? 0) === 1 ? '' : 's'}
            {result.classification ? `; classified as ${result.classification.verificationSource} (confidence ${result.classification.confidenceScore ?? ''}).` : '.'}
          </BodyText>
        </Card>
      )}

      <Card>
        <Heading level={3} as={3} size="card" className="mb-4">
          Pending connections
        </Heading>
        {queueError && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded text-error text-sm">{queueError}</div>
        )}
        <DataTable
          columns={queueColumns}
          data={queue}
          emptyMessage={loadingQueue ? 'Loading queue...' : 'No pending direct-attested connections.'}
        />
        <div className="mt-4">
          <Button variant="ghost" onClick={() => void loadQueue()} disabled={loadingQueue}>
            {loadingQueue ? 'Refreshing...' : 'Refresh queue'}
          </Button>
        </div>
      </Card>

      {selected && (
        <Card>
          <Heading level={3} as={3} size="card" className="mb-1">
            Keystroke positions
          </Heading>
          <BodyText>
            {selected.memberName ?? selected.userId}
            {selected.memberEmail ? ` (${selected.memberEmail})` : ''}
          </BodyText>

          <div className="mt-4 mb-4 max-w-xs">
            <FormLabel htmlFor="asOfDate">As-of date</FormLabel>
            <Input
              id="asOfDate"
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate((e.target as HTMLInputElement).value)}
            />
          </div>

          <div className="space-y-6">
            {positions.map((row, index) => (
              <div key={index} className="border-b border-recessed pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-start">
                  <Input
                    label="Account"
                    value={row.accountName}
                    onChange={(e) => updatePosition(index, 'accountName', (e.target as HTMLInputElement).value)}
                    placeholder="Brokerage"
                  />
                  <Input
                    label="Ticker"
                    value={row.tickerSymbol}
                    onChange={(e) => updatePosition(index, 'tickerSymbol', (e.target as HTMLInputElement).value)}
                    placeholder="AAPL"
                  />
                  <Input
                    label="Asset"
                    value={row.securityName}
                    onChange={(e) => updatePosition(index, 'securityName', (e.target as HTMLInputElement).value)}
                    placeholder="Apple Inc."
                  />
                  <Select
                    label="Asset class"
                    id={`assetClass-${index}`}
                    value={row.assetClass}
                    onChange={(e) => updatePosition(index, 'assetClass', e.target.value)}
                    placeholder="Select..."
                    options={ASSET_CLASS_OPTIONS as readonly SelectOption[] as SelectOption[]}
                  />
                  <Input
                    label="Quantity"
                    type="number"
                    value={row.quantity}
                    onChange={(e) => updatePosition(index, 'quantity', (e.target as HTMLInputElement).value)}
                    placeholder="100"
                  />
                  <div>
                    <Input
                      label="Unit price"
                      type="number"
                      value={row.unitPrice}
                      onChange={(e) => updatePosition(index, 'unitPrice', (e.target as HTMLInputElement).value)}
                      placeholder="195.00"
                    />
                    {row.recalcFlash === 'unitPrice' && (
                      <p className="mt-1 text-xs text-accent">Recalculated from market value / qty</p>
                    )}
                  </div>
                  <div>
                    <Input
                      label="Market value"
                      type="number"
                      value={row.marketValue}
                      onChange={(e) => updatePosition(index, 'marketValue', (e.target as HTMLInputElement).value)}
                      placeholder="19500"
                    />
                    {row.recalcFlash === 'marketValue' && (
                      <p className="mt-1 text-xs text-accent">Recalculated from qty x unit price</p>
                    )}
                  </div>
                </div>
                {/* LP-interest fund economics (GH #1838): only the advisor can
                    supply these; they feed pe-valuation's real LP-interest
                    assessment. Optional as a group -- left empty, the position
                    rides the static asset-class baseline. */}
                {LP_CLASSES.includes(row.assetClass) && (
                  <div className="mt-3 border-t border-recessed pt-3">
                    <p className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-2">
                      Fund economics (optional -- enables a real LP-interest valuation)
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                      <Input
                        label={`Commitment (${ATTESTED_POSITION_CURRENCY})`}
                        type="number"
                        value={row.feCommitmentAmount}
                        onChange={(e) => updatePosition(index, 'feCommitmentAmount', (e.target as HTMLInputElement).value)}
                        placeholder="5000000"
                      />
                      <Input
                        label="Capital called (%)"
                        type="number"
                        value={row.feCalledCapitalPct}
                        onChange={(e) => updatePosition(index, 'feCalledCapitalPct', (e.target as HTMLInputElement).value)}
                        placeholder="70"
                      />
                      <Input
                        label={`Fund NAV (${ATTESTED_POSITION_CURRENCY})`}
                        type="number"
                        value={row.feCurrentFundNav}
                        onChange={(e) => updatePosition(index, 'feCurrentFundNav', (e.target as HTMLInputElement).value)}
                        placeholder="320000000"
                      />
                      <Input
                        label="Vintage year"
                        type="number"
                        value={row.feVintageYear}
                        onChange={(e) => updatePosition(index, 'feVintageYear', (e.target as HTMLInputElement).value)}
                        placeholder="2021"
                      />
                      <Select
                        label="Strategy"
                        id={`feStrategy-${index}`}
                        value={row.feStrategy}
                        onChange={(e) => updatePosition(index, 'feStrategy', e.target.value)}
                        placeholder="Select..."
                        options={FUND_STRATEGY_OPTIONS as readonly SelectOption[] as SelectOption[]}
                      />
                    </div>
                    {fundEconomicsState(row) === 'partial' && (
                      <p className="mt-2 text-xs text-accent">
                        Fill commitment, capital called, and fund NAV together -- or clear all three to attest without them.
                      </p>
                    )}
                  </div>
                )}
                <div className="mt-2 flex justify-end">
                  <Button
                    variant="ghost"
                    onClick={() => removePosition(index)}
                    disabled={positions.length === 1}
                  >
                    Remove position
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <Button variant="secondary" onClick={addPosition}>
              Add position
            </Button>
            <Button variant="primary" onClick={openStepUp} disabled={!formValid}>
              Review and attest
            </Button>
          </div>
          {!formValid && (
            <p className="mt-3 text-sm text-tertiary">
              {selected === null
                ? 'Select a pending connection to begin.'
                : asOfDate.trim() === ''
                  ? 'Enter an as-of date to continue.'
                  : completeRows.length === 0
                    ? `Complete at least one position to continue${
                        positions.length > 0 && rowIncompleteReason(positions[0])
                          ? ` (row 1 needs ${rowIncompleteReason(positions[0])})`
                          : ''
                      }.`
                    : ''}
            </p>
          )}
          {!canStepUp && !mfaOff && (
            <p className="mt-3 text-sm text-error">
              Step-up is unavailable in this session. Sign out and sign back in with your authenticator to attest.
            </p>
          )}
        </Card>
      )}

      <Dialog open={stepUpOpen} onOpenChange={setStepUpOpen} title="Step-up and attest">
        <div className="space-y-4">
          <BodyText>
            You are attesting {completeRows.length} position{completeRows.length === 1 ? '' : 's'} for
            {' '}
            {selected?.memberName ?? selected?.userId}.
            {mfaOff
              ? ' Affirm the fiduciary attestation to submit. MFA is disabled for this tenant, so no authenticator code is required.'
              : ' Enter your authenticator code to re-assert your identity, then affirm the fiduciary attestation.'}
          </BodyText>

          {!mfaOff && (
            <div>
              <FormLabel htmlFor="stepUpCode">Authenticator code</FormLabel>
              <Input
                id="stepUpCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode((e.target as HTMLInputElement).value)}
                placeholder="123456"
              />
            </div>
          )}

          <Checkbox
            label="As a FINRA fiduciary, I attest that these positions are accurate as of the stated date."
            checked={fiduciary}
            onChange={setFiduciary}
          />

          {submitError && (
            <div className="p-3 bg-error/10 border border-error/20 rounded text-error text-sm">{submitError}</div>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={() => setStepUpOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void confirmAttest()} disabled={submitting || !fiduciary}>
              {submitting ? 'Attesting...' : 'Confirm attestation'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
